import React from 'react';
import { makeStyles } from '@material-ui/core';

import { Grid, FormControl, Select, Button } from '@ha/dsl';
import { useIntl } from 'ha/i18n';
import { RadiusValue } from 'ha/types/SearchFilters';
import MenuItem from 'ha/components/next/Menu/MenuItem';

import SearchAreaFilter, { Props as SearchAreaProps } from './SearchAreaFilter';
import PlaceFilter, { Props as PlaceFilterProps } from './PlaceFilter';

interface Props {
  placeProps: PlaceFilterProps;
  searchAreaProps: SearchAreaProps;
  radiusProps: {
    value: RadiusValue;
    onChange: (value: RadiusValue) => void;
  };
  hideReset?: boolean;
}

const useStyles = makeStyles(theme => ({
  defaultValueSelect: {
    color: theme.palette.grey['400'],
  },
  defaultValueOption: {
    display: 'none',
  },
  resetButton: {
    marginLeft: -theme.spacing(1),
    marginTop: -theme.spacing(2),
    textTransform: 'none',
    color: theme.palette.secondary.dark,
    justifyContent: 'flex-start',
  },
}));

const LocationFilter: React.FC<Props> = ({
  placeProps,
  searchAreaProps,
  radiusProps: { value: radiusValue, onChange: onRadiusChange },
  hideReset,
}) => {
  const { T } = useIntl();
  const classes = useStyles();

  const handleRadiusChange = React.useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      onRadiusChange(e.target.value as RadiusValue);
    },
    [onRadiusChange],
  );

  const handleRadiusClosed = React.useCallback(() => {
    if (document.activeElement instanceof HTMLElement) {
      document.activeElement.blur();
    }
  }, []);

  const handleResetClick = React.useCallback(() => {
    onRadiusChange(RadiusValue.DEFAULT);
  }, [onRadiusChange]);

  const isDefaultRadius = radiusValue === RadiusValue.DEFAULT;

  return (
    <Grid container>
      {!hideReset && !isDefaultRadius && (
        <Grid item>
          <Button
            variant="text"
            color="secondary"
            onClick={handleResetClick}
            className={classes.resetButton}
          >
            {T('search.filters.location.reset')}
          </Button>
        </Grid>
      )}
      <Grid item xs={12}>
        <PlaceFilter {...placeProps} />
      </Grid>
      <Grid item xs={12}>
        <FormControl margin="dense" fullWidth variant="outlined" hiddenLabel>
          <Select
            margin="dense"
            value={radiusValue}
            onChange={handleRadiusChange}
            className={isDefaultRadius ? classes.defaultValueSelect : undefined}
            MenuProps={{
              TransitionProps: {
                onExited: handleRadiusClosed,
              },
              anchorOrigin: {
                vertical: 'bottom',
                horizontal: 'left',
              },
            }}
          >
            <MenuItem
              value={RadiusValue.DEFAULT}
              disabled
              className={classes.defaultValueOption}
            >
              {T('search.filters.location.option_choose_distance')}
            </MenuItem>
            <MenuItem value={RadiusValue.R1}>
              {T('distance.within')} 1 km
            </MenuItem>
            <MenuItem value={RadiusValue.R5}>
              {T('distance.within')} 5 km
            </MenuItem>
            <MenuItem value={RadiusValue.R10}>
              {T('distance.within')} 10 km
            </MenuItem>
            <MenuItem value={RadiusValue.R15}>
              {T('distance.within')} 15 km
            </MenuItem>
            <MenuItem value={RadiusValue.R20}>
              {T('distance.within')} 20 km
            </MenuItem>
          </Select>
        </FormControl>
      </Grid>
      <Grid item xs={12}>
        <SearchAreaFilter {...searchAreaProps} />
      </Grid>
    </Grid>
  );
};

export default LocationFilter;
